import type { LinearCoordinate } from "../../../../lib/map-linear-extractor";

export type LinearAimQuery = {
  latitude: number;
  longitude: number;
  heading: number;
  horizontalFov: number;
  maxDistance: number;
};

export type LinearAimPoint = {
  latitude: number;
  longitude: number;
  distance: number;
  bearing: number;
  relativeAngle: number;
  alignmentScore: number;
  proximityScore: number;
};

type XY = { x: number; y: number };

const METERS_PER_DEGREE_LATITUDE = 110_900;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function normalizeDegrees(value: number) {
  return ((value % 360) + 360) % 360;
}

function signedDegrees(value: number) {
  return ((value + 540) % 360) - 180;
}

function localScale(latitude: number) {
  return Math.max(0.2, Math.cos((latitude * Math.PI) / 180));
}

function toLocalXY(originLatitude: number, originLongitude: number, coordinate: LinearCoordinate): XY {
  return {
    x: (coordinate[0] - originLongitude) * 111_320 * localScale(originLatitude),
    y: (coordinate[1] - originLatitude) * METERS_PER_DEGREE_LATITUDE,
  };
}

function toLonLat(originLatitude: number, originLongitude: number, point: XY) {
  return {
    latitude: originLatitude + point.y / METERS_PER_DEGREE_LATITUDE,
    longitude: originLongitude + point.x / (111_320 * localScale(originLatitude)),
  };
}

function add(a: XY, b: XY): XY { return { x: a.x + b.x, y: a.y + b.y }; }
function subtract(a: XY, b: XY): XY { return { x: a.x - b.x, y: a.y - b.y }; }
function multiply(a: XY, scalar: number): XY { return { x: a.x * scalar, y: a.y * scalar }; }
function dot(a: XY, b: XY) { return a.x * b.x + a.y * b.y; }
function cross(a: XY, b: XY) { return a.x * b.y - a.y * b.x; }
function lengthSquared(a: XY) { return dot(a, a); }

function rayIntersectionPoint(a: XY, b: XY, ray: XY): XY | null {
  const segment = subtract(b, a);
  const denominator = cross(segment, ray);
  if (Math.abs(denominator) < 1e-7) return null;
  // a + t*segment = s*ray, t in [0,1], s >= 0.
  const t = -cross(a, ray) / denominator;
  const s = cross(a, segment) / denominator;
  if (t < 0 || t > 1 || s < 0) return null;
  return add(a, multiply(segment, t));
}

function closestPointToOrigin(a: XY, b: XY): XY {
  const segment = subtract(b, a);
  const denominator = lengthSquared(segment);
  if (denominator <= 1e-9) return a;
  const t = clamp(-dot(a, segment) / denominator, 0, 1);
  return add(a, multiply(segment, t));
}

function proximityScore(distance: number) {
  if (distance <= 300) return 1;
  if (distance <= 750) return 0.86;
  if (distance <= 1_500) return 0.62;
  if (distance <= 2_500) return 0.32;
  if (distance <= 3_000) return 0.16;
  return 0;
}

function evaluatePoint(point: XY, query: LinearAimQuery): LinearAimPoint | null {
  const distance = Math.hypot(point.x, point.y);
  if (!Number.isFinite(distance) || distance < 0.5 || distance > query.maxDistance) return null;

  const bearing = normalizeDegrees((Math.atan2(point.x, point.y) * 180) / Math.PI);
  const relativeAngle = signedDegrees(bearing - query.heading);
  const broadAim = Math.min(query.horizontalFov / 2, Math.max(8, query.horizontalFov * 0.22));
  if (Math.abs(relativeAngle) > broadAim) return null;

  const alignmentScore = Math.max(0, 1 - Math.abs(relativeAngle) / broadAim);
  const ll = toLonLat(query.latitude, query.longitude, point);
  return {
    ...ll,
    distance,
    bearing,
    relativeAngle,
    alignmentScore,
    proximityScore: proximityScore(distance),
  };
}

export function findBestPointOnLinearGeometry(
  lines: readonly LinearCoordinate[][],
  query: LinearAimQuery,
): LinearAimPoint | null {
  const headingRadians = (normalizeDegrees(query.heading) * Math.PI) / 180;
  const ray = { x: Math.sin(headingRadians), y: Math.cos(headingRadians) };
  const evaluated: LinearAimPoint[] = [];

  for (const line of lines) {
    for (let index = 1; index < line.length; index += 1) {
      const a = toLocalXY(query.latitude, query.longitude, line[index - 1]);
      const b = toLocalXY(query.latitude, query.longitude, line[index]);
      const candidates = [a, b, closestPointToOrigin(a, b)];
      const intersection = rayIntersectionPoint(a, b, ray);
      if (intersection) candidates.push(intersection);

      for (const point of candidates) {
        const match = evaluatePoint(point, query);
        if (match) evaluated.push(match);
      }
    }
  }

  evaluated.sort((left, right) => {
    const leftScore = left.alignmentScore * 0.68 + left.proximityScore * 0.32;
    const rightScore = right.alignmentScore * 0.68 + right.proximityScore * 0.32;
    return rightScore - leftScore
      || Math.abs(left.relativeAngle) - Math.abs(right.relativeAngle)
      || left.distance - right.distance;
  });
  return evaluated[0] ?? null;
}
